import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/lib/auth";
import { initAnalytics, pageview } from "@/lib/analytics";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/Nav";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import Calculator from "@/pages/Calculator";
import Scanner from "@/pages/Scanner";
import Dashboard from "@/pages/Dashboard";
import MediaKitEditor from "@/pages/MediaKitEditor";
import PublicMediaKit from "@/pages/PublicMediaKit";
import Copilot from "@/pages/Copilot";
import Pipeline from "@/pages/Pipeline";
import Inbox from "@/pages/Inbox";
import Playbooks from "@/pages/Playbooks";
import PlaybookDetail from "@/pages/PlaybookDetail";
import TrendRadar from "@/pages/TrendRadar";
import Outbound from "@/pages/Outbound";
import Billing from "@/pages/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

const RESERVED = new Set([
  "login",
  "signup",
  "calculator",
  "scanner",
  "app",
  "dashboard",
  "api",
  "billing",
  "payment",
  "playbooks",
  "auth",
]);

const Private = ({ children }) => {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <div className="p-8 text-neutral-500 font-mono">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
};

// Handles /:handle route — if handle is a reserved word, treats as 404-ish
const HandleRoute = () => {
  const parts = window.location.pathname.split("/").filter(Boolean);
  const handle = parts[0] || "";
  const cleaned = handle.replace(/^@/, "");
  if (!cleaned || RESERVED.has(cleaned.toLowerCase())) {
    return <Navigate to="/" replace />;
  }
  return <PublicMediaKit />;
};

function AppShell() {
  const loc = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Synchronous detection during render — avoids race with AuthProvider's /auth/me check
  const isAuthCallback = typeof window !== "undefined" && window.location.hash.includes("session_id=");
  useEffect(() => {
    initAnalytics();
  }, []);
  useEffect(() => {
    if (!isAuthCallback) pageview(loc.pathname);
  }, [loc.pathname, isAuthCallback]);
  if (isAuthCallback) return <AuthCallback />;
  return (
    <div className="App min-h-screen">
      <Nav />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/calculator" element={<Calculator />} />
        <Route path="/scanner" element={<Scanner />} />
        <Route path="/playbooks" element={<Playbooks />} />
        <Route path="/playbooks/:slug" element={<PlaybookDetail />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />

        <Route
          path="/app/dashboard"
          element={
            <Private>
              <Dashboard />
            </Private>
          }
        />
        <Route
          path="/app/mediakit"
          element={
            <Private>
              <MediaKitEditor />
            </Private>
          }
        />
        <Route
          path="/app/copilot"
          element={
            <Private>
              <Copilot />
            </Private>
          }
        />
        <Route
          path="/app/inbox"
          element={
            <Private>
              <Inbox />
            </Private>
          }
        />
        <Route
          path="/app/playbooks"
          element={
            <Private>
              <Playbooks />
            </Private>
          }
        />
        <Route
          path="/app/playbooks/:slug"
          element={
            <Private>
              <PlaybookDetail />
            </Private>
          }
        />
        <Route
          path="/app/radar"
          element={
            <Private>
              <TrendRadar />
            </Private>
          }
        />
        <Route
          path="/app/outbound"
          element={
            <Private>
              <Outbound />
            </Private>
          }
        />
        <Route
          path="/app/pipeline"
          element={
            <Private>
              <Pipeline />
            </Private>
          }
        />

        {/* Legacy alias */}
        <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />

        {/* Public media kit: /@handle or /handle */}
        <Route path="/:handle" element={<HandleRoute />} />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppShell />
          <Toaster theme="dark" position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
